import { useState, type InputHTMLAttributes } from 'react'
import { Field, FIELD_CONTROL, FIELD_INVALID } from './Field'
import { Icon } from './Icon'
import { cx } from '@/lib/cx'

interface Props extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id' | 'type'> {
  label: string
  required?: boolean
  error?: string | undefined
  hint?: string | undefined
}

/** Typing a long password blind, one-handed, is the commonest reason a sign-in
 *  fails twice — so it reveals on demand. */
export function PasswordField({ label, required, error, hint, className, ...rest }: Props) {
  const [revealed, setRevealed] = useState(false)

  return (
    <Field label={label} required={required ?? false} error={error} hint={hint}>
      {({ id, describedBy, invalid }) => (
        <div className="relative flex items-center">
          <input
            id={id}
            // Flipped, never a second plain-text input: swapping elements loses
            // the caret, the value, and the password-manager binding.
            type={revealed ? 'text' : 'password'}
            className={cx(FIELD_CONTROL, 'pr-touch', invalid && FIELD_INVALID, className)}
            aria-invalid={invalid || undefined}
            aria-describedby={describedBy}
            {...rest}
          />
          <button
            type="button"
            className="absolute right-0.5 grid min-h-touch w-touch place-items-center rounded-md text-fg-subtle transition-colors duration-150 ease-standard hover:text-fg aria-pressed:text-accent"
            // Not "toggle password": the name says what pressing it will do.
            aria-label={revealed ? 'Hide password' : 'Show password'}
            aria-pressed={revealed}
            onClick={() => setRevealed((current) => !current)}
          >
            <Icon name={revealed ? 'eyeOff' : 'eye'} />
          </button>
        </div>
      )}
    </Field>
  )
}
